import React, {useState} from "react";
import {AdNetworkDestination, AdNetworkMatchConditions, PresenceCondition} from "../../http/protocol";
import {t} from "../../i18n";
import {errorMessage, fromConditionList, toConditionList} from "./adNetworkUtils";

interface Props {
    initial: AdNetworkDestination;
    onSave: (destination: AdNetworkDestination) => Promise<void>;
    onCancel: () => void;
}

/** Templates pre-filled by the preset buttons. {host} is left for the operator to replace. */
const PRESETS: { name: string; signup: string; ftd: string }[] = [
    {
        name: "Voluum",
        signup: "https://{host}/postback?cid={click_id}&et={event}",
        ftd: "https://{host}/postback?cid={click_id}&payout={amount_eur}&txid={txid}&et={event}"
    },
    {
        // BeMob: cid is the only mandatory param; conversion types (signup vs FTD) are
        // distinguished via cet (custom conversion event) — NOT status, which only accepts
        // the lifecycle values new/approved/declined/chargeback.
        name: "BeMob",
        signup: "https://{host}/postback?cid={click_id}&cet={event}",
        ftd: "https://{host}/postback?cid={click_id}&payout={amount_eur}&txid={txid}&cet={event}"
    },
    {
        // Keitaro: subid + status are mandatory; registration and deposit are built-in
        // conversion statuses, so they are sent as literals rather than via {event}.
        // {host} must include the postback key path, e.g. tracker.example.com/AbCdEf.
        name: "Keitaro",
        signup: "https://{host}/postback?subid={click_id}&status=registration",
        ftd: "https://{host}/postback?subid={click_id}&status=deposit&payout={amount_eur}&currency=EUR&tid={txid}"
    },
    {
        // Binom: conversions go through click.php with cnv_id as the click id;
        // cnv_status is free-form and mapped in Binom's status schemes.
        name: "Binom",
        signup: "https://{host}/click.php?cnv_id={click_id}&cnv_status={event}",
        ftd: "https://{host}/click.php?cnv_id={click_id}&payout={amount_eur}&cnv_currency=EUR&cnv_status={event}"
    },
    {
        // RedTrack: clickid + sum; type must match a conversion type defined in
        // RedTrack under Tools → Conversion tracking → Conversion types.
        name: "RedTrack",
        signup: "https://{host}/postback?clickid={click_id}&type={event}",
        ftd: "https://{host}/postback?clickid={click_id}&sum={amount_eur}&type={event}"
    }
];

export const DestinationForm = ({initial, onSave, onCancel}: Props) => {
    const [destination, setDestination] = useState<AdNetworkDestination>({...initial});
    const [channelIn, setChannelIn] = useState(fromConditionList(initial.matchConditions?.channelIn));
    const [sourceIn, setSourceIn] = useState(fromConditionList(initial.matchConditions?.sourceIn));
    const [campaignIn, setCampaignIn] = useState(fromConditionList(initial.matchConditions?.campaignIn));
    const [error, setError] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);

    const set = (changes: Partial<AdNetworkDestination>) => setDestination(prev => ({...prev, ...changes}));
    const setConditions = (changes: Partial<AdNetworkMatchConditions>) =>
        setDestination(prev => ({...prev, matchConditions: {...prev.matchConditions, ...changes}}));

    const applyPreset = (preset: typeof PRESETS[0]) => {
        set({urlTemplateSignup: preset.signup, urlTemplateFtd: preset.ftd});
    };

    const save = () => {
        setSaving(true);
        setError(undefined);
        const payload: AdNetworkDestination = {
            ...destination,
            matchConditions: {
                ...destination.matchConditions,
                channelIn: toConditionList(channelIn),
                sourceIn: toConditionList(sourceIn),
                campaignIn: toConditionList(campaignIn)
            }
        };
        onSave(payload)
            .catch(e => setError(errorMessage(e, t("ad-networks.form.save-error", undefined, "Failed to save destination"))))
            .finally(() => setSaving(false));
    };

    const presenceSelect = (id: string, value: PresenceCondition | undefined, onChange: (value?: PresenceCondition) => void) => (
        <select id={id} data-testid={id} className="form-control" value={value ?? ""}
                onChange={e => onChange(e.target.value === "" ? undefined : e.target.value as PresenceCondition)}>
            <option value="">{t("ad-networks.condition.any", undefined, "Any")}</option>
            <option value={PresenceCondition.PRESENT}>{t("ad-networks.condition.present", undefined, "Must be present")}</option>
            <option value={PresenceCondition.ABSENT}>{t("ad-networks.condition.absent", undefined, "Must be absent")}</option>
        </select>
    );

    return (
        <div className="card-box" data-testid="ad-network-destination-form">
            <h4>{destination.id
                ? t("ad-networks.form.edit-title", undefined, "Edit destination") + ": " + (initial.name ?? "")
                : t("ad-networks.form.new-title", undefined, "New destination")}</h4>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="form-group">
                <label htmlFor="ad-network-name">{t("ad-networks.form.name", undefined, "Name")}</label>
                <input id="ad-network-name" data-testid="ad-network-name" className="form-control" value={destination.name ?? ""}
                       onChange={e => set({name: e.target.value})}/>
            </div>

            <div className="form-group">
                <label>{t("ad-networks.form.presets", undefined, "Presets")}</label>
                <div>
                    {PRESETS.map(preset => (
                        <button key={preset.name} type="button" className="btn btn-sm btn-secondary mr-2"
                                data-testid={"ad-network-preset-" + preset.name.toLowerCase()}
                                onClick={() => applyPreset(preset)}>
                            {preset.name}
                        </button>
                    ))}
                    <small className="text-muted ml-2">
                        {t("ad-networks.form.presets-help", undefined, "Pre-fills both URL templates — replace {host} with your tracker domain.")}
                    </small>
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="ad-network-signup-url">{t("ad-networks.form.signup-template", undefined, "Signup callback URL template")}</label>
                <input id="ad-network-signup-url" data-testid="ad-network-signup-url" className="form-control" value={destination.urlTemplateSignup ?? ""}
                       placeholder="https://trk.example.com/postback?cid={click_id}&et={event}"
                       onChange={e => set({urlTemplateSignup: e.target.value})}/>
            </div>

            <div className="form-group">
                <label htmlFor="ad-network-ftd-url">{t("ad-networks.form.ftd-template", undefined, "First deposit (FTD) callback URL template")}</label>
                <input id="ad-network-ftd-url" data-testid="ad-network-ftd-url" className="form-control" value={destination.urlTemplateFtd ?? ""}
                       placeholder="https://trk.example.com/postback?cid={click_id}&payout={amount_eur}&txid={txid}&et={event}"
                       onChange={e => set({urlTemplateFtd: e.target.value})}/>
            </div>

            <div className="form-group form-check">
                <input className="form-check-input" type="checkbox" id="ad-network-enabled" data-testid="ad-network-enabled"
                       checked={destination.enabled ?? false} onChange={e => set({enabled: e.target.checked})}/>
                <label className="form-check-label" htmlFor="ad-network-enabled">{t("ad-networks.form.enabled", undefined, "Enabled")}</label>
            </div>

            <h5>{t("ad-networks.form.conditions-title", undefined, "Match conditions")}</h5>
            <p className="text-muted">
                {t("ad-networks.form.conditions-help", undefined, "All conditions must match for the callback to fire. Leave a condition empty to ignore it.")}
            </p>
            <div className="row">
                <div className="col-md-3 form-group">
                    <label htmlFor="ad-network-cond-clickid">{t("ad-networks.form.cond-click-id", undefined, "click_id")}</label>
                    {presenceSelect("ad-network-cond-clickid", destination.matchConditions?.clickId, v => setConditions({clickId: v}))}
                </div>
                <div className="col-md-3 form-group">
                    <label htmlFor="ad-network-cond-ref">{t("ad-networks.form.cond-ref", undefined, "Affiliate tracker (ref)")}</label>
                    {presenceSelect("ad-network-cond-ref", destination.matchConditions?.ref, v => setConditions({ref: v}))}
                </div>
            </div>
            <div className="row">
                <div className="col-md-4 form-group">
                    <label htmlFor="ad-network-cond-ch">{t("ad-networks.form.cond-ch", undefined, "Channel (ch) is one of")}</label>
                    <input id="ad-network-cond-ch" data-testid="ad-network-cond-ch" className="form-control" value={channelIn} placeholder="fb, ig, tiktok"
                           onChange={e => setChannelIn(e.target.value)}/>
                </div>
                <div className="col-md-4 form-group">
                    <label htmlFor="ad-network-cond-src">{t("ad-networks.form.cond-src", undefined, "Source (src) is one of")}</label>
                    <input id="ad-network-cond-src" data-testid="ad-network-cond-src" className="form-control" value={sourceIn} placeholder="gamereviews.com"
                           onChange={e => setSourceIn(e.target.value)}/>
                </div>
                <div className="col-md-4 form-group">
                    <label htmlFor="ad-network-cond-ca">{t("ad-networks.form.cond-ca", undefined, "Campaign (ca) is one of")}</label>
                    <input id="ad-network-cond-ca" data-testid="ad-network-cond-ca" className="form-control" value={campaignIn} placeholder="oma1, oma2"
                           onChange={e => setCampaignIn(e.target.value)}/>
                </div>
            </div>

            <button className="btn btn-primary mr-2" data-testid="ad-network-save" disabled={saving} onClick={save}>
                {saving ? t("ad-networks.form.saving", undefined, "Saving...") : t("button.save", undefined, "Save")}
            </button>
            <button className="btn btn-secondary" data-testid="ad-network-cancel" disabled={saving} onClick={onCancel}>
                {t("button.cancel", undefined, "Cancel")}
            </button>
        </div>
    );
};
